import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { throwError } from "rxjs";
import { catchError, map, retry, retryWhen, delay, take, concatMap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { LoginResponse } from "../models/loginResponse";

// DTOs for authentication methods
export interface EmailAuthenticationMethodDto {
  id?: string;
  email: string;
  isRegistered?: boolean;
}

export interface PhoneAuthenticationMethodDto {
  id?: string;
  phoneNumber: string;
  isRegistered?: boolean;
}

export interface AuthenticationMethodsResultDto {
  hasMethodEnabled: boolean;
  emailMethod: EmailAuthenticationMethodDto;
  mobilePhoneMethod: PhoneAuthenticationMethodDto;
}

@Injectable({
  providedIn: "root",
})
export class UserApiService {
  constructor(private http: HttpClient) {}

  changeEmail(params: { confirmEmail: string; email: string }) {
    const body = {
      NewEmail: params?.email,
      NewEmailConfirm: params?.confirmEmail,
    };

    return this.http
      .post<{
        Message?: string;
      }>(environment.moeUserApiProxyUrl + "/profiles/change-email", body)
      .pipe(
        map((response) => {
          if (!response || !response.Message) {
            return { success: true };
          }
          return {
            message: response.Message,
          };
        }),
        catchError((error: any) => {
          // Try to extract a message from the API response
          let message =
            error?.error?.error ||
            (typeof error.error === 'string' ? error.error : null) ||
            error?.message ||
            'An unknown error occurred.';
          return throwError({ message });
        })
      );
  }

  addAccountOrPolicy(params: {
    accountOrPolicyNumber: string;
    policyZipCode: string;
  }) {
    const body = {
      AccountOrPolicyNumber: params?.accountOrPolicyNumber,
      Zip: params?.policyZipCode,
    };

    return this.http
      .post<{ error?: string }>(
        `${environment.moeUserApiProxyUrl}/profiles/attach-account-policy`,
        body
      )
      .pipe(
        map((response) => {
          console.log('Add account/policy response:', response);
          // Handle case where API returns 200 with no body
          if (response === null || response === undefined) {
            console.log('API returned successful response with no body');
            return { success: true }; // Return a success indicator
          }
          if (response?.error) {
            throw new Error(response?.error || "Unknown error occurred");
          }
          return { success: true }; // Return consistent success response
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('Error in addAccountOrPolicy:', error);
          const message =
            typeof error.error === "string"
              ? error.error
              : error?.error?.error || "An unknown error occurred.";

          return throwError(message);
        })
      );
  }

  removePolicy(params: { policyNumber: string }) {
    const body = {
      policyNumber: params?.policyNumber,
    };

    return this.http
      .post<{ error?: string }>(
        `${environment.moeUserApiProxyUrl}/profiles/detach-policy`,
        body
      )
      .pipe(
        map((response) => {
          console.log('Remove policy response:', response);
          // Handle case where API returns 200 with no body
          if (response === null || response === undefined) {
            console.log('API returned successful response with no body');
            return { success: true }; // Return a success indicator
          }
          if (response?.error) {
            throw new Error(response?.error || "Unknown error occurred");
          }
          return { success: true }; // Return consistent success response
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('Error in removePolicy:', error);
          const message =
            typeof error.error === "string"
              ? error.error
              : error?.error?.error || "An unknown error occurred.";

          return throwError(message);
        })
      );
  }


  getProfileAsync(): Promise<LoginResponse> {
      console.log('loadProfileAsync called from UserApiService');
    return this.http
      .get<LoginResponse>(`${environment.moeUserApiProxyUrl}/profiles`)
      .pipe(
        retryWhen(errors =>
          errors.pipe(
            concatMap((error: HttpErrorResponse, index) => {
              // Only retry on network errors (status 0) or server errors (5xx)
              // Don't retry on 4xx client errors (auth, forbidden, etc.)
              const shouldRetry = error.status === 0 || (error.status >= 500 && error.status < 600);
              const maxRetries = 3;
              
              if (shouldRetry && index < maxRetries) {
                // Exponential backoff: 1s, 2s, 4s
                const delayMs = Math.pow(2, index) * 1000;
                console.warn(`Profile API call failed (attempt ${index + 1}/${maxRetries + 1}), retrying in ${delayMs}ms...`, error);
                return throwError(error).pipe(delay(delayMs));
              }
              
              // Don't retry - pass the error through
              return throwError(error);
            }),
            take(3) // Maximum 3 retry attempts
          )
        ),
        catchError((err: HttpErrorResponse) => {
          const code = err.status;
          const backendMsg =
            typeof err.error === "string"
              ? err.error
              : err.error?.error_description || err.error?.error || err.message;

          const message =
            code === 0
              ? "Network error. Please check your connection."
              : code === 401
              ? "Not authenticated."
              : code === 403
              ? "Not authorized."
              : backendMsg || "An unknown error occurred.";

          return throwError(() => ({ status: code, message }));
        })
      ).toPromise();
  }
    
  deleteProfile() {
    const body = {
      sendEmail: true,
      serviceCall: false,
    };

    return this.http
      .post<{
        code: string;
        message: string;
      }>(environment.moeUserApiProxyUrl + "/profiles/delete-profile", body)
      .pipe(
        map((response) => {
          if (!response || !response.message) {
            return { success: true };
          }
          return {
            message: response.message,
          };
        })
      );
  }

  getAuthenticationMethodsStatus() {
    const bustCache = `cb=${Date.now()}`;
    const url = `${environment.moeUserApiProxyUrl}/profiles/authentication-methods?${bustCache}`;
    return this.http
      .get<AuthenticationMethodsResultDto>(url)
      .pipe(
        map((response) => response),
        catchError((error: any) => {
          let message = error?.error?.error || error?.message || 'Unable to fetch authentication methods.';
          return throwError({ message });
        })
      );
  }

  updateAuthenticationMethods(payload: {
    profileId?: number;
    emailMethod: { id?: string; email: string; isRegistered?: boolean };
    mobilePhoneMethod: { id?: string; phoneNumber: string; isRegistered?: boolean };
  }) {
    const body: any = {
      EmailMethod: payload.emailMethod,
      MobilePhoneMethod: payload.mobilePhoneMethod,
    };
    if (payload.profileId) {
      body.ProfileId = payload.profileId;
    }
    return this.http
      .post<AuthenticationMethodsResultDto>(
        `${environment.moeUserApiProxyUrl}/profiles/update-authentication-methods`,
        body
      )
      .pipe(
        map((response) => response),
        catchError((error: any) => {
          let message = error?.error?.error || error?.message || 'Unable to update authentication methods.';
          return throwError({ message });
        })
      );
  }
}
